import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { Spinner } from 'react-bootstrap';
import api from '../../../api';
import './bug-kanban-board.scss';

interface BugReport {
  id: string;
  threadId: string | null;
  agentId: string | null;
  reporterUserId: string;
  companyId: number;
  companyName: string;
  severity: string;
  title: string;
  description: string;
  stepsToReproduce: string | null;
  expectedBehavior: string | null;
  actualBehavior: string | null;
  errorMessages: string | null;
  affectedFeatures: string[] | null;
  status: BugStatus;
  timesSeen: number;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

type BugStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

interface BugReportsResponse {
  success: boolean;
  columns: Record<BugStatus, BugReport[]>;
  statuses: readonly BugStatus[];
}

const COLUMN_LABELS: Record<BugStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc3545',
  high: '#fd7e14',
  medium: '#ffc107',
  low: '#28a745',
  untriaged: '#6c757d',
};

const BugKanbanBoard: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [columns, setColumns] = useState<Record<BugStatus, BugReport[]>>({
    open: [],
    in_progress: [],
    resolved: [],
    closed: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBugId, setExpandedBugId] = useState<string | null>(null);

  const fetchBugs = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<BugReportsResponse>('/bug-reports', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setColumns(response.data.columns);
      } else {
        setError('Failed to load bug reports');
      }
    } catch (err) {
      console.error('Error fetching bug reports:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load bug reports',
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const sourceStatus = source.droppableId as BugStatus;
      const destStatus = destination.droppableId as BugStatus;

      // Optimistically update UI
      setColumns((prev) => {
        const newColumns = { ...prev };
        const sourceItems = [...newColumns[sourceStatus]];
        const [movedItem] = sourceItems.splice(source.index, 1);

        if (sourceStatus === destStatus) {
          sourceItems.splice(destination.index, 0, movedItem);
          newColumns[sourceStatus] = sourceItems;
        } else {
          const destItems = [...newColumns[destStatus]];
          movedItem.status = destStatus;
          destItems.splice(destination.index, 0, movedItem);
          newColumns[sourceStatus] = sourceItems;
          newColumns[destStatus] = destItems;
        }

        return newColumns;
      });

      // If status changed, update backend
      if (sourceStatus !== destStatus) {
        try {
          const token = await getAccessTokenSilently();
          await api.patch(
            `/bug-reports/${draggableId}/status`,
            { status: destStatus },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (err) {
          console.error('Error updating bug status:', err);
          // Revert on error
          fetchBugs();
        }
      }
    },
    [getAccessTokenSilently, fetchBugs],
  );

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const toggleExpand = (bugId: string) => {
    setExpandedBugId(expandedBugId === bugId ? null : bugId);
  };

  if (loading) {
    return (
      <div className='bug-kanban-loading'>
        <Spinner animation='border' size='sm' />
        <p>Loading bug reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bug-kanban-error'>
        <p>{error}</p>
      </div>
    );
  }

  const totalBugs = Object.values(columns).flat().length;

  return (
    <div className='bug-kanban-board'>
      <div className='kanban-header'>
        <h2>Bug Tracker</h2>
        <span className='bug-count'>{totalBugs} bugs</span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className='kanban-columns'>
          {(Object.keys(COLUMN_LABELS) as BugStatus[]).map((status) => (
            <div key={status} className='kanban-column'>
              <div className='column-header'>
                <span className='column-title'>{COLUMN_LABELS[status]}</span>
                <span className='column-count'>{columns[status].length}</span>
              </div>

              <Droppable droppableId={status}>
                {(
                  provided: DroppableProvided,
                  snapshot: DroppableStateSnapshot,
                ) => (
                  <div
                    className={`column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {columns[status].map((bug, index) => (
                      <Draggable
                        key={bug.id}
                        draggableId={bug.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            className={`bug-card ${snapshot.isDragging ? 'dragging' : ''} ${expandedBugId === bug.id ? 'expanded' : ''}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => toggleExpand(bug.id)}
                          >
                            <div className='bug-card-header'>
                              <span
                                className='severity-badge'
                                style={{
                                  backgroundColor:
                                    SEVERITY_COLORS[bug.severity] || '#6c757d',
                                }}
                              >
                                {bug.severity}
                              </span>
                              {bug.timesSeen > 1 && (
                                <span className='times-seen'>
                                  ×{bug.timesSeen}
                                </span>
                              )}
                            </div>

                            <h4 className='bug-title'>{bug.title}</h4>

                            <div className='bug-meta'>
                              <span className='company'>{bug.companyName}</span>
                              <span className='date'>
                                {formatDate(bug.createdAt)}
                              </span>
                            </div>

                            {expandedBugId === bug.id && (
                              <div className='bug-details'>
                                <p className='description'>{bug.description}</p>

                                {bug.stepsToReproduce && (
                                  <div className='detail-section'>
                                    <strong>Steps to Reproduce:</strong>
                                    <p>{bug.stepsToReproduce}</p>
                                  </div>
                                )}

                                {bug.expectedBehavior && (
                                  <div className='detail-section'>
                                    <strong>Expected:</strong>
                                    <p>{bug.expectedBehavior}</p>
                                  </div>
                                )}

                                {bug.actualBehavior && (
                                  <div className='detail-section'>
                                    <strong>Actual:</strong>
                                    <p>{bug.actualBehavior}</p>
                                  </div>
                                )}

                                {bug.errorMessages && (
                                  <div className='detail-section error-messages'>
                                    <strong>Error:</strong>
                                    <code>{bug.errorMessages}</code>
                                  </div>
                                )}

                                {bug.affectedFeatures &&
                                  bug.affectedFeatures.length > 0 && (
                                    <div className='affected-features'>
                                      {bug.affectedFeatures.map(
                                        (feature, i) => (
                                          <span key={i} className='feature-tag'>
                                            {feature}
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  )}

                                {bug.threadId && bug.agentId && (
                                  <a
                                    href={`/conversations?agent_id=${bug.agentId}&thread_id=${bug.threadId}`}
                                    className='view-thread-link'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    View Thread →
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

export default BugKanbanBoard;
